import { useState } from "react";
import {
  Ban,
  Check,
  CircleDot,
  Hammer,
  Info,
  Loader2,
  TriangleAlert,
} from "lucide-react";

import type { EstimateDetailViewItem } from "@shared-core/data/api-types";
import {
  outcomeAllowsJobCreation,
  useLiveEstimateVocabulary,
  type EstimateOutcomeGroup,
  type EstimateOutcomeOption,
  type EstimateWriteIntent,
} from "@shared-core/domain/estimate-controls";

import { Badge } from "../../primitives/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { OutcomeConfirmStep } from "./OutcomeConfirmStep";

/**
 * Recording how an estimate ended — the control a CSR reaches for when they
 * would otherwise say "cancel it".
 *
 * Offers won, lost and superseded only. The still-live reasons ("Tender",
 * "No Feedback", "On hold with customer") are deliberately not here — they say
 * where the conversation stands, not how it ended, and they live under the
 * neighbouring Park / chase control so a CSR cannot park a quote believing
 * they closed it.
 *
 * Superseded asks for one thing the others do not: the estimate the work moved
 * to. Tharstern will not take a supersession without it, which is why this
 * group used to be offered as unavailable. It is collected on the confirm step.
 *
 * Two-step pick-then-confirm rather than a single dropdown because the
 * consequence differs per reason and is not guessable from the wording —
 * reasons differ on whether Tharstern will still let a job be raised
 * afterwards. The confirm step exists to state that, and to make the write.
 */

const GROUP_ICON = {
  lost: Ban,
  still_open: CircleDot,
  won: Check,
  superseded: Hammer,
} as const;

type Props = {
  estimate: EstimateDetailViewItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called once a status has actually been written to Tharstern. */
  onRecorded: (intent: EstimateWriteIntent) => void;
};

function OutcomeGroup({
  group,
  onPick,
}: {
  group: EstimateOutcomeGroup;
  onPick: (option: EstimateOutcomeOption) => void;
}) {
  const Icon = GROUP_ICON[group.outcomeClass];
  const blocked = group.requiresInstead !== null;
  return (
    <section aria-label={group.heading}>
      <div className="flex items-start gap-2 mb-1.5">
        <Icon className="w-4 h-4 shrink-0 mt-0.5 text-muted-foreground" />
        <div className="min-w-0">
          <h4 className="text-sm font-semibold">{group.heading}</h4>
          <p className="text-xs text-muted-foreground mt-0.5">
            {group.explanation}
          </p>
        </div>
      </div>
      {blocked ? (
        // The honesty convention: this cannot be done here, and the reason is
        // a Tharstern precondition, not a policy we chose. Say which, and
        // point at the control that can.
        <p className="ml-6 flex items-start gap-2 rounded-md border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>{group.requiresInstead}</span>
        </p>
      ) : (
        <ul className="ml-6 space-y-1">
          {group.options.map((option) => (
            <li key={option.statusRef}>
              <button
                type="button"
                onClick={() => onPick(option)}
                className="w-full text-left flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-[color:var(--color-surface-2,rgba(0,0,0,0.03))] transition-colors"
              >
                <span className="flex-1 min-w-0 truncate">
                  {option.statusRef}
                </span>
                {/* Which options the business actually uses. A reason with no
                    use in six months is offered, but not presented as normal.
                    `null` (a live-only string the 180-day snapshot never saw)
                    renders neither badge nor count — an unknown count is not
                    the same claim as a known zero. */}
                {option.liveRowCount === 0 ? (
                  <Badge variant="neutral">Not used recently</Badge>
                ) : option.liveRowCount === null ? null : (
                  <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                    {option.liveRowCount.toLocaleString()} in 6 months
                  </span>
                )}
                {!outcomeAllowsJobCreation(option) && (
                  <Badge variant="warning">Blocks a job</Badge>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * The picker step's own subcomponent, so `useLiveEstimateVocabulary`'s fetch
 * mounts (and refetches) exactly when Radix mounts this dialog's content —
 * see the hook's own docstring.
 */
function OutcomePicker({
  onPick,
}: {
  onPick: (option: EstimateOutcomeOption) => void;
}) {
  const vocabulary = useLiveEstimateVocabulary();

  if (vocabulary.status === "loading") {
    return (
      <p className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading Tharstern&rsquo;s current status list…
      </p>
    );
  }

  if (vocabulary.status === "error") {
    return (
      <p className="flex items-start gap-2 rounded-md border border-[color:var(--color-error)] px-3 py-2 text-sm text-[color:var(--color-error)]">
        <TriangleAlert className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          Could not load Tharstern&rsquo;s current status list, so nothing here
          can be safely offered. Close this and try again.
        </span>
      </p>
    );
  }

  return (
    <div className="space-y-5 py-2">
      {vocabulary.outcomeGroups.map((group) => (
        <OutcomeGroup key={group.outcomeClass} group={group} onPick={onPick} />
      ))}
    </div>
  );
}

export function RecordOutcomeDialog({
  estimate,
  open,
  onOpenChange,
  onRecorded,
}: Props) {
  const [picked, setPicked] = useState<EstimateOutcomeOption | null>(null);
  const sourceEstimateId = estimate.information.source_estimate_id;

  function close() {
    onOpenChange(false);
    setPicked(null);
  }

  // Unreachable in practice: EstimateControls only mounts this dialog once
  // estimateControlsPresentation has confirmed a source id exists (the
  // Record outcome button is disabled-with-a-reason otherwise). An explicit
  // guard rather than a non-null assertion, so a future caller that skips
  // that check gets nothing rendered instead of a write aimed at nothing.
  if (sourceEstimateId == null) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
        else onOpenChange(true);
      }}
    >
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        {picked === null ? (
          <>
            <DialogHeader>
              <DialogTitle>Record the outcome</DialogTitle>
              <DialogDescription>
                Pick the reason the business already uses — it is what the
                win/loss reporting is built from, so the wording matters more
                than it looks. Where the conversation stands, rather than how it
                ended, lives under Park / chase.
              </DialogDescription>
            </DialogHeader>
            <OutcomePicker onPick={setPicked} />
          </>
        ) : (
          <OutcomeConfirmStep
            // Keyed so going back and picking a different reason starts a
            // fresh confirm step rather than reusing stale local state.
            key={picked.statusRef}
            estimate={estimate}
            sourceEstimateId={sourceEstimateId}
            option={picked}
            onBack={() => setPicked(null)}
            onConfirm={(intent) => {
              onRecorded(intent);
              close();
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
